import {TaskLeadingVisual} from '@github-ui/agent-sessions/components/TaskLeadingVisual'
import {type IssueAgentTask, useIssueAgentTasks} from '@github-ui/agent-sessions/hooks/use-issue-agent-tasks'
import {isSessionState} from '@github-ui/agent-sessions/types/session'
import {getTaskTitle} from '@github-ui/agent-sessions/utils/task-helpers'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {CopilotIcon} from '@primer/octicons-react'
import {clsx} from 'clsx'
import type {RefObject} from 'react'
import {useCallback, useRef, useState} from 'react'

import {AgentTaskSidePanel} from './AgentTaskSidePanel'
import styles from './IssueAgentTasksSection.module.css'

type IssueAgentTasksSectionProps = {
  owner: string
  repo: string
  issueNumber: number
  className?: string
  testId?: string
}

/**
 * Renders a compact list of agent tasks for an issue.
 * Placed between the issue body and the sub-issues list in the issue detail view.
 * Only renders when there are tasks associated with the issue.
 */
export function IssueAgentTasksSection({owner, repo, issueNumber, className, testId}: IssueAgentTasksSectionProps) {
  const tasksQuery = useIssueAgentTasks({owner, repo, issueNumber})
  const tasks = tasksQuery.data?.tasks ?? []
  const [selectedTask, setSelectedTask] = useState<IssueAgentTask | null>(null)
  const sidePanelEnabled = isFeatureEnabled('issue_cca_task_side_panel')
  const returnFocusRef = useRef<HTMLButtonElement | null>(null)

  if (!isFeatureEnabled('issue_cca_visualization_session_panel')) return null
  if (tasksQuery.isLoading) {
    return null
  }
  const containerClassName = clsx(styles.container, className)

  if (tasksQuery.error) {
    return (
      <div className={containerClassName} data-testid={testId ?? 'issue-agent-tasks-section'}>
        <div className={styles.taskStatus}>Unable to load Copilot coding agent tasks.</div>
      </div>
    )
  }

  if (tasks.length === 0) {
    return null
  }

  return (
    <div className={containerClassName} data-testid={testId ?? 'issue-agent-tasks-section'}>
      <ul className={styles.taskList}>
        {tasks.map(task => (
          <AgentTaskItem
            key={task.id}
            task={task}
            sidePanelEnabled={sidePanelEnabled}
            onSelectTask={setSelectedTask}
            returnFocusRef={returnFocusRef}
          />
        ))}
      </ul>
      {selectedTask && (
        <AgentTaskSidePanel
          task={selectedTask}
          open
          onClose={() => setSelectedTask(null)}
          returnFocusRef={returnFocusRef}
        />
      )}
    </div>
  )
}

function AgentTaskItem({
  task,
  sidePanelEnabled,
  onSelectTask,
  returnFocusRef,
}: {
  task: IssueAgentTask
  sidePanelEnabled: boolean
  onSelectTask: (task: IssueAgentTask) => void
  returnFocusRef: RefObject<HTMLButtonElement | null>
}) {
  const status = isSessionState(task.status) ? task.status : undefined
  const title = getTaskTitle(task)
  const buttonRef = useRef<HTMLButtonElement>(null)

  const handleClick = useCallback(() => {
    returnFocusRef.current = buttonRef.current
    onSelectTask(task)
  }, [task, onSelectTask, returnFocusRef])

  return (
    <li className={styles.taskItem}>
      <div className={styles.taskTopRow}>
        <div className={styles.taskTitle}>
          {sidePanelEnabled ? (
            <button ref={buttonRef} type="button" className={styles.taskTitleButton} onClick={handleClick}>
              {title}
            </button>
          ) : task.url ? (
            <a href={task.url} className={styles.taskTitleLink}>
              {title}
            </a>
          ) : (
            title
          )}
        </div>

        <div className={styles.taskControls}>
          {status && <TaskLeadingVisual sessionState={status} className={styles.statusIcon} />}
          <CopilotIcon className={styles.agentIcon} />
        </div>
      </div>
    </li>
  )
}
